import { ChevronDownIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import { useState } from 'react';

// Configuration object for cart UI component settings
export const CART_UI_CONFIG = {
  // Animation timing
  animation: {
    duration: {
      enter: 300,
      leave: 200
    }
  },
  // Feedback messages
  messages: {
    emptyCart: 'Your cart is empty',
    outOfStock: 'Out of stock',
    lowStock: 'Low stock',
    inStock: 'In stock',
    checkoutButton: 'CHECK OUT',
    addToCartButton: 'Add to cart'
  },
  // UI customization
  colors: {
    primary: '#14BBD3',
    primaryHover: '#13adc3',
    primaryActive: '#119cb0',
    grayText: '#73777F',
    darkText: '#212529',
    lightGray: '#EDEEF0',
    mediumGray: '#CCD2D9',
    border: '#DDE2E8'
  }
};

// Type definitions for props
interface QuantityButtonProps {
  type: 'plus' | 'minus';
  onClick: () => void;
  disabled?: boolean;
  quantity?: number;
  inventoryAvailable?: number;
}

interface VariantOptionSelectorProps {
  option: {
    name: string;
    values: string[];
  };
  selectedValue: string | undefined;
  availableValues: string[];
  onChange: (value: string) => void;
  isDisabled?: boolean;
}

interface RecommendedItemProps {
  image: string;
  title: string;
  description: string;
  price: string;
  comparePrice?: string;
  onClick: () => void;
}

/**
 * Reusable button component for quantity adjustment
 */
export function QuantityButton({
  type,
  onClick,
  disabled = false,
  quantity,
  inventoryAvailable
}: QuantityButtonProps) {
  // Determine inventory status for better UX feedback
  const getInventoryStatusClass = () => {
    if (type === 'plus' && inventoryAvailable !== undefined) {
      if (quantity && quantity >= inventoryAvailable) {
        return 'cursor-not-allowed bg-red-50 opacity-60';
      }
      if (quantity && inventoryAvailable - quantity < 3) {
        return 'bg-yellow-50';
      }
    }
    return 'bg-white hover:bg-gray-100';
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={type === 'plus' ? 'Increase item quantity' : 'Reduce item quantity'}
      className={`relative flex h-[31px] w-[31px] items-center justify-center rounded-[2px] transition-colors ${
        disabled ? 'cursor-not-allowed opacity-50' : getInventoryStatusClass()
      }`}
    >
      {type === 'plus' ? (
        <PlusIcon className="h-4 w-4 text-black" />
      ) : (
        <MinusIcon className="h-4 w-4 text-black" />
      )}
    </button>
  );
}

/**
 * Option Selector component for cart items
 */
export function VariantOptionSelector({
  option,
  selectedValue,
  availableValues,
  onChange,
  isDisabled = false
}: VariantOptionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-[185px]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isDisabled || availableValues.length <= 1}
        className={`mb-[1px] flex h-[22px] w-full items-center justify-between rounded-[2px] border border-[#DDE2E8] bg-white px-[10px] py-[8px] text-left ${
          isDisabled || availableValues.length <= 1
            ? 'cursor-default opacity-80'
            : 'cursor-pointer hover:border-[#bbc3cb]'
        }`}
      >
        <span className="font-futura-book flex-grow truncate text-[12px] leading-[24px] text-[#495057]">
          {option.name}: {selectedValue || ''}
        </span>
        {availableValues.length > 1 && !isDisabled && (
          <ChevronDownIcon className="h-3 w-3 flex-shrink-0 text-[#495057]" />
        )}
      </button>

      {isOpen && availableValues.length > 1 && !isDisabled && (
        <div className="absolute top-full right-0 left-0 z-10 mt-1 max-h-[150px] overflow-y-auto rounded-[2px] border border-[#DDE2E8] bg-white shadow-lg">
          {availableValues.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                onChange(value);
                setIsOpen(false);
              }}
              className={`w-full px-[10px] py-[6px] text-left text-[12px] leading-[16px] ${
                value === selectedValue
                  ? 'font-futura-medium bg-[#F8F9FA] text-[#212529]'
                  : 'font-futura-book text-[#495057] hover:bg-[#F8F9FA]'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Reusable component for the "You May Also Like" item
 */
export function RecommendedItem({
  image,
  title,
  description,
  price,
  comparePrice,
  onClick
}: RecommendedItemProps) {
  return (
    <div className="flex w-full flex-col bg-[#F8F9FA] p-[14px_18px]">
      <div className="flex w-full items-center justify-between">
        <div className="flex max-w-[327px] items-center gap-[10px]">
          <div className="relative h-[93px] w-[97px] overflow-hidden">
            <Image
              src={image}
              alt={title}
              width={97}
              height={93}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex w-[220px] flex-col gap-[11px]">
            <div className="flex flex-col gap-[4px]">
              <h3 className="font-futura-book text-[16px] leading-[16px] font-normal text-[#212529]">
                {title}
              </h3>
              <p className="font-futura-book text-[12px] leading-[16px] font-normal text-[#73777F]">
                {description}
              </p>
            </div>
            <div className="flex items-center gap-[9px]">
              <span className="font-futura-medium text-[16px] leading-[16px] font-normal text-black">
                {price}
              </span>
              {comparePrice && (
                <span className="font-futura-medium text-[14px] leading-[16px] font-normal text-[#CCD2D9] line-through">
                  {comparePrice}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onClick}
          className="flex h-[32px] w-[107px] items-center justify-center rounded-[4px] border-[0.75px] border-[#CCD2D9] bg-white px-[16px] py-[4px] transition-colors hover:bg-gray-50 active:bg-gray-100"
        >
          <span className="font-futura-medium text-[12px] leading-[24px] font-normal text-[#212529]">
            {CART_UI_CONFIG.messages.addToCartButton}
          </span>
        </button>
      </div>
    </div>
  );
}

function getLocaleForCurrency(code: string): string {
  switch (code) {
    case 'CAD':
      return 'en-CA';
    case 'USD':
    default:
      return 'en-US';
  }
}

export function formatCurrency(amount: string | number, currencyCode = 'USD'): string {
  const locale = getLocaleForCurrency(currencyCode);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2
  }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
}

/**
 * Utility functions for pricing calculations
 */
export const PriceUtils = {
  /**
   * Calculate price with discount applied
   */
  calculateDiscountedPrice(originalPrice: string | number, discountRate = 0.35): number {
    const price = typeof originalPrice === 'string' ? parseFloat(originalPrice) : originalPrice;
    return price * (1 - discountRate);
  },

  /**
   * Calculate original price from discounted price
   */
  calculateOriginalPrice(discountedPrice: string | number, discountRate = 0.35): number {
    const price =
      typeof discountedPrice === 'string' ? parseFloat(discountedPrice) : discountedPrice;
    return price / (1 - discountRate);
  },

  /**
   * Calculate savings amount based on discounted price
   */
  calculateSavings(discountedPrice: string | number, discountRate = 0.35): number {
    const price =
      typeof discountedPrice === 'string' ? parseFloat(discountedPrice) : discountedPrice;
    const originalPrice = PriceUtils.calculateOriginalPrice(price, discountRate);
    return originalPrice - price;
  },

  /**
   * Calculate item savings (alias for calculateSavings for backward compatibility)
   */
  calculateItemSavings(discountedPrice: string | number, discountRate = 0.35): number {
    return PriceUtils.calculateSavings(discountedPrice, discountRate);
  }
};
